import {
  DesignLoopTypeOptions,
  DisplacementDirectionOptions,
  EnergyCharacteristicsData,
  FilterTypeOptions,
  GUFDeliveryContextOptions,
  IndicationYesNoOptions,
  InstallationFunctionOptions,
  LegalTypeOptions,
  QualityRegimeOptions,
  RelativeTemperatureOptions,
  UsageTypeOptions,
  WellFunctionOptions,
  designLoopTypeOptions,
  displacementDirectionOptions,
  filterTypeOptions,
  gufDeliveryContextOptions,
  indicationYesNoOptions,
  installationFunctionOptions,
  legalTypeOptions,
  qualityRegimeOptions,
  relativeTemperatureOptions,
  usageTypeOptions,
  wellFunctionOptions,
} from "@nens/brostar/uploadapi-client";

import { addSchema } from "@/helpers/schema-utils";

export const GUF_PRIMITIVE_FIELD_SCHEMA_LIBRARY = {
  // Metadata fields
  projectNumber: addSchema<string>({ type: "string", title: "Projectnummer" }),
  broId: addSchema<string>({ type: "string", title: "BRO-ID" }),
  qualityRegime: addSchema<QualityRegimeOptions>({
    type: "string",
    title: "Kwaliteitsregime",
    enum: qualityRegimeOptions.map((opt) => opt.value),
  }),
  deliveryAccountableParty: addSchema<string>({ type: "string", title: "Bronhouder [KVK]" }),
  correctionReason: addSchema<string>({
    type: "string",
    title: "Correctie reden",
  }),

  // GUF StartRegistration top-level fields
  objectIdAccountableParty: addSchema<string>({ type: "string", title: "Interne naam" }),
  deliveryContext: addSchema<GUFDeliveryContextOptions>({
    type: "string",
    title: "Kader aanlevering",
    enum: gufDeliveryContextOptions.map((opt) => opt.value),
  }),
  startTime: addSchema<string>({
    type: "string",
    title: "Begintijd",
    description: "Formaat: JJJJ-MM-DD, JJJJ-MM, of JJJJ",
  }),
  identificationLicence: addSchema<string>({ type: "string", title: "Vergunning kenmerk" }),
  legalType: addSchema<LegalTypeOptions>({
    type: "string",
    title: "Rechtstype",
    enum: legalTypeOptions.map((opt) => opt.value),
  }),
  primaryUsageType: addSchema<UsageTypeOptions>({
    type: "string",
    title: "Primair gebruiksdoel",
    enum: usageTypeOptions.map((opt) => opt.value),
  }),
  secondaryUsageTypes: addSchema<UsageTypeOptions[]>({
    type: "array",
    title: "Secundaire gebruiksdoelen",
    items: {
      type: "string",
      title: "Gebruiksdoel",
      enum: usageTypeOptions.map((opt) => opt.value),
    },
  }),
  humanConsumption: addSchema<IndicationYesNoOptions>({
    type: "string",
    title: "Menselijk verbruik",
    enum: indicationYesNoOptions.map((opt) => opt.value),
    default: "nee",
  }),

  // LicensedQuantity fields
  licensedInOut: addSchema<DisplacementDirectionOptions>({
    type: "string",
    title: "Richting",
    enum: displacementDirectionOptions.map((opt) => opt.value),
  }),
  maximumPerHour: addSchema<number>({
    type: "number",
    title: "Maximum per uur (m³/u)",
    minimum: 0,
  }),
  maximumPerDay: addSchema<number>({
    type: "number",
    title: "Maximum per dag (m³/d)",
    minimum: 0,
  }),
  maximumPerMonth: addSchema<number>({
    type: "number",
    title: "Maximum per maand (m³/mnd)",
    minimum: 0,
  }),
  maximumPerQuarter: addSchema<number>({
    type: "number",
    title: "Maximum per kwartaal (m³/kwt)",
    minimum: 0,
  }),
  maximumPerYear: addSchema<number>({
    type: "number",
    title: "Maximum per jaar (m³/jr)",
    minimum: 0,
  }),

  // DesignInstallation fields
  designInstallationId: addSchema<string>({ type: "string", title: "Installatie ID" }),
  designInstallationPos: addSchema<string>({ type: "string", title: "Locatie (x y)" }),
  installationFunction: addSchema<InstallationFunctionOptions>({
    type: "string",
    title: "Installatiefunctie",
    enum: installationFunctionOptions.map((opt) => opt.value),
    default: "onttrekking",
  }),
  energyCharacteristics: addSchema<EnergyCharacteristicsData>({
    type: "object",
    title: "Energiekarakteristieken",
    properties: {
      energyCold: { type: "number", title: "Energie koud per jaar (MWh/jr)", minimum: 0 },
      energyWarm: { type: "number", title: "Energie warm per jaar (MWh/jr)", minimum: 0 },
      maximumInfiltrationTemperatureWarm: {
        type: "number",
        title: "Max. infiltratietemperatuur warm (°C)",
        minimum: 0,
      },
      averageInfiltrationTemperatureCold: {
        type: "number",
        title: "Gem. infiltratietemperatuur koud (°C)",
        minimum: 0,
      },
      averageInfiltrationTemperatureWarm: {
        type: "number",
        title: "Gem. infiltratietemperatuur warm (°C)",
        minimum: 0,
      },
      powerCold: { type: "number", title: "Bodemzijdig vermogen koud (kW)", minimum: 0 },
      powerWarm: { type: "number", title: "Bodemzijdig vermogen warm (kW)", minimum: 0 },
      power: { type: "number", title: "Bodemzijdig vermogen (kW)", minimum: 0 },
      averageColdWater: {
        type: "number",
        title: "Gem. jaarvolume koud (m³/jr)",
        minimum: 0,
      },
      averageWarmWater: {
        type: "number",
        title: "Gem. jaarvolume warm (m³/jr)",
        minimum: 0,
      },
      maximumYearQuantityCold: {
        type: "number",
        title: "Max. jaarvolume koud (m³/jr)",
        minimum: 0,
      },
      maximumYearQuantityWarm: {
        type: "number",
        title: "Max. jaarvolume warm (m³/jr)",
        minimum: 0,
      },
    },
    required: [],
  }),

  // DesignWell fields
  designWellId: addSchema<string>({ type: "string", title: "Put ID" }),
  wellFunctions: addSchema<WellFunctionOptions[]>({
    type: "array",
    title: "Putfuncties",
    minItems: 1,
    items: {
      type: "string",
      title: "Putfunctie",
      enum: wellFunctionOptions.map((opt) => opt.value),
    },
  }),
  height: addSchema<number>({ type: "number", title: "Hoogte (m)" }),
  wellPos: addSchema<string>({ type: "string", title: "Locatie (x y)" }),
  geometryPubliclyAvailable: addSchema<IndicationYesNoOptions>({
    type: "string",
    title: "Geometrie openbaar",
    enum: indicationYesNoOptions.map((opt) => opt.value),
    default: "nee",
  }),
  maximumWellDepth: addSchema<number>({
    type: "number",
    title: "Maximale putdiepte (m)",
    minimum: 0,
  }),
  maximumWellDepthPubliclyAvailable: addSchema<IndicationYesNoOptions>({
    type: "string",
    title: "Maximale diepte openbaar",
    enum: indicationYesNoOptions.map((opt) => opt.value),
    default: "nee",
  }),
  maximumWellCapacity: addSchema<number>({
    type: "number",
    title: "Maximale putcapaciteit (m³/u)",
    minimum: 0,
  }),
  relativeTemperature: addSchema<RelativeTemperatureOptions>({
    type: "string",
    title: "Relatieve temperatuur",
    enum: relativeTemperatureOptions.map((opt) => opt.value),
  }),

  // DesignScreen fields
  screenType: addSchema<FilterTypeOptions>({
    type: "string",
    title: "Filtertype",
    enum: filterTypeOptions.map((opt) => opt.value),
  }),
  designScreenTop: addSchema<number>({
    type: "number",
    title: "Bovenkant filter (m tov bkb)",
  }),
  designScreenBottom: addSchema<number>({
    type: "number",
    title: "Onderkant filter (m tov bkb)",
  }),
  designScreenGeometry: addSchema<string>({
    type: "string",
    title: "Filter geometrie",
    enum: ["Point", "LineString"],
  }),

  // DesignLoop fields
  designLoopId: addSchema<string>({ type: "string", title: "Bodelus ID" }),
  designLoopPos: addSchema<string>({ type: "string", title: "Locatie (x y)" }),
  loopType: addSchema<DesignLoopTypeOptions>({
    type: "string",
    title: "Bodemlustype",
    enum: designLoopTypeOptions.map((opt) => opt.value),
  }),
  startDate: addSchema<string>({ type: "string", title: "Begindatum", format: "date" }),
  endDate: addSchema<string>({ type: "string", title: "Einddatum", format: "date" }),
  geometryType: addSchema<"Point" | "LineString">({
    type: "string",
    title: "Geometrietype",
    enum: ["Point", "LineString"],
    default: "Point",
  }),

  // DesignSurfaceInfiltration fields
  designSurfaceInfiltrationId: addSchema<string>({
    type: "string",
    title: "Oppervlakte-infiltratie ID",
  }),
  designSurfaceInfiltrationPos: addSchema<string>({
    type: "string",
    title: "Locatie (polygoon coördinaten)",
  }),

  // ── Realised installation fields ─────────────────────────────────────────
  realisedInstallationId: addSchema<string>({
    type: "string",
    title: "Gerealiseerde installatie ID",
  }),
  realisedLoopPos: addSchema<string>({ type: "string", title: "Locatie (x y)" }),
  startValidity: addSchema<string>({
    type: "string",
    title: "Begin geldigheid",
    description: "Formaat: JJJJ-MM-DD, JJJJ-MM, of JJJJ",
  }),

  // ── Realised well fields ─────────────────────────────────────────────────
  realisedWellId: addSchema<string>({ type: "string", title: "Gerealiseerde put ID" }),
  wellDepth: addSchema<number>({ type: "number", title: "Putdiepte (m)", minimum: 0 }),
  publiclyAvailable: addSchema<IndicationYesNoOptions>({
    type: "string",
    title: "Openbaar beschikbaar",
    enum: indicationYesNoOptions.map((opt) => opt.value),
    default: "nee",
  }),
  validity: addSchema<string>({
    type: "string",
    title: "Geldigheid",
    description: "Formaat: JJJJ-MM-DD, JJJJ-MM, of JJJJ",
  }),
  lifespan: addSchema<string>({
    type: "string",
    title: "Levensduur",
    description: "Formaat: JJJJ-MM-DD/JJJJ-MM-DD",
  }),

  // ── Realised loop fields ─────────────────────────────────────────────────
  realisedLoopId: addSchema<string>({ type: "string", title: "Gerealiseerde bodelus ID" }),
  endDepthBottom: addSchema<number>({
    type: "number",
    title: "Einddiepte onderkant (m)",
    minimum: 0,
  }),
  segments: addSchema<string>({ type: "string", title: "Segmenten (curve geometrie)" }),
  realisedGeometryType: addSchema<"Point" | "Curve">({
    type: "string",
    title: "Geometrietype",
    enum: ["Point", "Curve"],
    default: "Point",
  }),

  // ── Realised screen fields ───────────────────────────────────────────────
  realisedScreenId: addSchema<string>({ type: "string", title: "Gerealiseerde filter ID" }),
  topScreenDepth: addSchema<number>({ type: "number", title: "Bovenkant filter diepte (m)" }),
  length: addSchema<number>({ type: "number", title: "Filterlengte (m)", minimum: 0 }),
  realisedScreenPos: addSchema<string>({ type: "string", title: "Locatie (x y)" }),

  // ── Realised surface infiltration fields ─────────────────────────────────
  realisedSurfaceInfiltrationId: addSchema<string>({
    type: "string",
    title: "Gerealiseerde oppervlakte-infiltratie ID",
  }),
  realisedSurfaceInfiltrationPos: addSchema<string>({
    type: "string",
    title: "Locatie (polygoon coördinaten)",
  }),

  // ── Event / date fields ──────────────────────────────────────────────────
  eventDate: addSchema<string>({
    type: "string",
    title: "Gebeurtenisdatum",
    description: "Formaat: JJJJ-MM-DD, JJJJ-MM, of JJJJ",
  }),
  endTime: addSchema<string>({
    type: "string",
    title: "Eindtijd",
    description: "Formaat: JJJJ-MM-DD, JJJJ-MM, of JJJJ",
  }),
};

type PrimitiveFieldSchemaLibraryType = typeof GUF_PRIMITIVE_FIELD_SCHEMA_LIBRARY;

export function pickGufPrimitiveLibraryFields<K extends keyof PrimitiveFieldSchemaLibraryType>(
  fieldNames: readonly K[]
): Pick<PrimitiveFieldSchemaLibraryType, K> {
  const result = {} as Pick<PrimitiveFieldSchemaLibraryType, K>;
  for (const name of fieldNames) {
    result[name] = GUF_PRIMITIVE_FIELD_SCHEMA_LIBRARY[name];
  }
  return result;
}
